import { useState, useRef, useEffect, useCallback } from "react";
import axios from "axios";
import { MessageCircle, X, Send, Leaf, Check } from "lucide-react";
import { BRAND } from "../data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function getSessionId() {
  let id = localStorage.getItem("verra_chat_session");
  if (!id) {
    id = (crypto.randomUUID && crypto.randomUUID()) || `sess-${Date.now()}-${Math.random().toString(16).slice(2)}`;
    localStorage.setItem("verra_chat_session", id);
  }
  return id;
}

const WELCOME = {
  role: "assistant",
  content: `Hi! I'm the ${BRAND.name} assistant. Ask me about our bagasse products, sizes, certifications, MOQ or export — or say "I'd like a quote".`,
};

const SUGGESTIONS = [
  "Which products are microwave safe?",
  "What certifications do you have?",
  "I'd like a quote",
  "Do you ship to my country?",
];

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([WELCOME]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const sessionId = useRef(getSessionId());
  const scrollRef = useRef(null);

  const scrollToBottom = useCallback(() => {
    requestAnimationFrame(() => {
      if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    });
  }, []);

  useEffect(() => { if (open) scrollToBottom(); }, [messages, open, scrollToBottom]);

  useEffect(() => {
    if (!open) return;
    axios.get(`${API}/chat/${sessionId.current}`).then(({ data }) => {
      if (data.messages && data.messages.length) {
        setMessages(data.messages.map((m) => ({ role: m.role, content: m.content })));
      }
    }).catch(() => {});
  }, [open]);

  const send = async (textArg) => {
    const text = (typeof textArg === "string" ? textArg : input).trim();
    if (!text || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }]);
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/chat`, { session_id: sessionId.current, message: text });
      setMessages((m) => {
        const next = [...m, { role: "assistant", content: data.reply }];
        if (data.lead_captured) {
          next.push({ role: "system", content: "Your details were shared with our team — we'll be in touch shortly." });
        }
        return next;
      });
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", content: "Sorry, I'm having trouble right now. Please try again or use the quote form." }]);
    } finally {
      setLoading(false);
    }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
  };

  return (
    <div className="fixed left-5 bottom-5 z-50 flex flex-col items-start gap-3">
      {open && (
        <div data-testid="chat-panel" className="w-[92vw] max-w-[380px] h-[520px] max-h-[75vh] bg-white rounded-3xl shadow-2xl border border-verra-blue/10 flex flex-col overflow-hidden">
          <div className="bg-verra-blue px-5 py-4 flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <span className="w-9 h-9 rounded-full bg-verra-green flex items-center justify-center">
                <Leaf className="w-4.5 h-4.5 text-white" />
              </span>
              <div className="leading-tight">
                <p className="text-white font-display text-lg">Verraleaf Assistant</p>
                <p className="text-verra-light text-[11px]">Typically replies instantly</p>
              </div>
            </div>
            <button data-testid="chat-close" onClick={() => setOpen(false)} className="text-white/80 hover:text-white" aria-label="Close chat">
              <X className="w-5 h-5" />
            </button>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-verra-base" data-lenis-prevent>
            {messages.map((m, i) => (
              m.role === "system" ? (
                <div key={i} data-testid="chat-lead-confirm" className="flex justify-center">
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-verra-green/15 text-verra-green text-xs font-medium px-3 py-1.5">
                    <Check className="w-3.5 h-3.5" /> {m.content}
                  </span>
                </div>
              ) : (
                <div key={i} data-testid="chat-message" className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
                    m.role === "user" ? "bg-verra-blue text-white rounded-br-sm" : "bg-white border border-verra-blue/10 text-verra-blue rounded-bl-sm"
                  }`}>
                    {m.content}
                  </div>
                </div>
              )
            ))}

            {messages.length <= 1 && !loading && (
              <div className="flex flex-wrap gap-2 pt-1" data-testid="chat-suggestions">
                {SUGGESTIONS.map((s) => (
                  <button key={s} onClick={() => send(s)}
                    className="rounded-full border border-verra-green/30 bg-white text-verra-green text-xs font-medium px-3 py-1.5 hover:bg-verra-green hover:text-white transition-colors">
                    {s}
                  </button>
                ))}
              </div>
            )}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-white border border-verra-blue/10 rounded-2xl rounded-bl-sm px-4 py-3">
                  <span className="flex gap-1">
                    <span className="w-2 h-2 bg-verra-green/60 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                    <span className="w-2 h-2 bg-verra-green/60 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                    <span className="w-2 h-2 bg-verra-green/60 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                  </span>
                </div>
              </div>
            )}
          </div>

          <div className="p-3 border-t border-verra-blue/10 bg-white flex items-center gap-2">
            <input
              data-testid="chat-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKey}
              placeholder="Ask about products, MOQ, export…"
              className="flex-1 rounded-full border border-verra-blue/15 bg-verra-base px-4 py-2.5 text-sm text-verra-blue outline-none focus:border-verra-green transition-colors"
            />
            <button data-testid="chat-send" onClick={send} disabled={loading || !input.trim()}
              className="w-10 h-10 shrink-0 rounded-full bg-verra-green text-white flex items-center justify-center hover:bg-verra-blue transition-colors disabled:opacity-50" aria-label="Send">
              <Send className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      <button
        data-testid="chat-toggle"
        onClick={() => setOpen((o) => !o)}
        aria-label="Open Verraleaf assistant"
        className="inline-flex items-center gap-2 rounded-full bg-verra-blue text-white pl-4 pr-5 py-3 shadow-xl hover:bg-verra-green transition-colors duration-300"
      >
        {open ? <X className="w-5 h-5" /> : <MessageCircle className="w-5 h-5" />}
        <span className="text-sm font-semibold hidden sm:inline">Ask Verraleaf</span>
      </button>
    </div>
  );
}
